import React, { useEffect, useState } from "react";
import { useAxiosInstance } from "../../../services/Interceptor";
import LoadingSpinner from "../../../components/LoadingSpinner";
import Search from "../../common/events/SearchEvents";
import EventList from "../../coach/dashboard/EventList";
import AttendeesModal from "../../coach/dashboard/AttendeesModal";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  eventErrorAction,
  eventLoadingAction,
  eventsSearchAction,
} from "../../../store/slices/eventSlice";
import {
  SearchEventsByParams,
  getAttendees,
} from "../../../services/EventsService";
import { useResponsiveHeight } from "../../../hooks/useResponsiveHeight";
import Modal from "../../../components/Modal";
import AthleteScore from "../../../components/athlete-scores/athlete-score";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import AthleteSearchPanel from "../../common/athletes/AthleteSearchPanel";

function UniversityDashboard() {
  const dispatch = useDispatch();
  const authUser = useAuthUser();
  const { user } = authUser;

  const [showResults, setShowResults] = useState(false);
  const [selectedResultAthlete, setSelectedResultAthlete] = useState(null);

  const r_events = useSelector((state) => state.events.events);
  const loadingEvents = useSelector((state) => state.events.loading);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [loadingAttendees, setLoadingAttendees] = useState(false);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const { adjustedHeight } = useResponsiveHeight({
    mobileHeight: 340,
    desktopHeight: 480,
  });
  const apiInstance = useAxiosInstance();

  const loadEvents = async (query = "") => {
    dispatch(eventLoadingAction(true));
    try {
      const response = await SearchEventsByParams(apiInstance, query);
      dispatch(eventsSearchAction(response));
    } catch (err) {
      dispatch(eventErrorAction(err.message || "An error occurred"));
    } finally {
      dispatch(eventLoadingAction(false));
    }
  };

  useEffect(() => {
    loadEvents("");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (query) => {
    loadEvents(query);
  };

  const toggleModal = async (event = null) => {
    if (event) {
      setLoadingAttendees(true);
      try {
        const data = await getAttendees(apiInstance, event.id);
        setAttendees(data);
        setSelectedEvent(event);
      } catch (err) {
        toast.error(err.message || "Error loading attendees");
      } finally {
        setLoadingAttendees(false);
      }
    } else {
      setSelectedEvent(null);
      setAttendees([]);
    }
  };

  return (
    <>
      <div className="flex-grow pb-20 xl:pb-0 lg:pb-0">
        <div className="p-4 h-full bg-gray-100">
          <div className="flex flex-col xl:flex-row lg:flex-row gap-4">
            <div className="w-full lg:w-2/4 xl:w-2/4">
              <div className="p-4 bg-white shadow-lg rounded-lg border-gray-300">
                <h2 className="text-xl mb-5 uppercase">SHARED PROFILE</h2>
                <AthleteSearchPanel
                  authUser={authUser}
                  adjustedHeight={adjustedHeight + 140}
                  listVariant="chips"
                />
              </div>
            </div>

            <div className="w-full lg:w-2/4 xl:w-2/4">
              <div className="bg-white shadow-lg rounded-lg p-4 pb-0 border-gray-300 h-full">
                <h2 className="text-xl mb-5 uppercase">EVENTS</h2>
                <Search onSearch={handleSearch} placeholder="Search Events" />
                {loadingEvents ? (
                  <LoadingSpinner />
                ) : (
                  <div
                    className="space-y-3 overflow-auto pr-4 -mr-4"
                    style={{ height: `${adjustedHeight + 140}px` }}
                  >
                    {r_events.length > 0 ? (
                      r_events.map((event, index) => (
                        <EventList
                          key={index}
                          event={event}
                          onViewAttendees={() => toggleModal(event)}
                          showAttendeesButton={
                            user.role === "university" || user.role === "admin"
                          }
                        />
                      ))
                    ) : (
                      <div className="w-full p-2 mt-5 flex flex-col items-center">
                        <img
                          alt="No events that match your search"
                          src="/nodata.gif"
                          className="object-cover h-20 w-20 rounded-full border-gray-300 border"
                        />
                        <span className="mt-4 text-center text-md text-slate-600">
                          There are no events that match your search
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {selectedEvent && (
        <AttendeesModal
          event={selectedEvent}
          attendees={attendees}
          loading={loadingAttendees}
          onClose={() => toggleModal()}
          setSelectedAthlete={setSelectedAthlete}
          onViewResults={(athlete) => {
            setSelectedResultAthlete(athlete);
            setShowResults(true);
          }}
          userRole={user.role}
        />
      )}

      {selectedAthlete && (
        <Modal
          isOpen={true}
          onClose={() => setSelectedAthlete(null)}
          backdrop="static"
          keyboard={false}
        >
          <AthleteScore
            userId={selectedAthlete.userId}
            showOffers={true}
            showTrackittScore={true}
            showPositionScore={true}
            searchEvent={false}
            showProfile={true}
            showRank={true}
            showLikes={true}
          />
        </Modal>
      )}

      {showResults && selectedResultAthlete && (
        <Modal
          isOpen={true}
          onClose={() => {
            setShowResults(false);
            setSelectedResultAthlete(null);
          }}
          backdrop="static"
          keyboard={false}
        >
          <h2 className="text-xl mb-4 font-semibold pt-4 px-2">
            Results for {selectedResultAthlete.name} - {selectedEvent.name}
          </h2>
          <AthleteScore
            userId={selectedResultAthlete.userId}
            showOffers={false}
            showTrackittScore={true}
            showPositionScore={true}
            searchEvent={false}
            showProfile={false}
            showRank={true}
            showLikes={false}
            eventId={selectedEvent.id}
          />
        </Modal>
      )}
    </>
  );
}

export default UniversityDashboard;
